import type { ActivityType } from "@lifelog/shared";
import { ChevronDown, ChevronUp, Pin } from "lucide-react";
import { ActivityIcon } from "./icons.js";

export function CustomizePanel({
  types,
  onPin,
  onUnpin,
  onMove,
  busyId,
}: {
  types: ActivityType[];
  onPin: (typeId: string) => void;
  onUnpin: (typeId: string) => void;
  onMove: (typeId: string, direction: "up" | "down") => void;
  busyId?: string | null;
}) {
  const pinned = types
    .filter((t) => t.pinned)
    .sort((a, b) => (a.pinOrder ?? 0) - (b.pinOrder ?? 0));
  const rest = types
    .filter((t) => !t.pinned)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Pinned
        </h3>
        {pinned.length === 0 ? (
          <p className="text-sm italic text-gray-400">Nothing pinned yet — pin activities below to keep them first.</p>
        ) : (
          <ul className="space-y-2">
            {pinned.map((t, i) => (
              <li
                key={t.activityTypeId}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
              >
                <ActivityIcon name={t.iconName} size={18} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t.name}
                </span>
                <button
                  type="button"
                  aria-label={`Move ${t.shortName} up`}
                  disabled={i === 0 || busyId === t.activityTypeId}
                  onClick={() => onMove(t.activityTypeId, "up")}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                >
                  <ChevronUp size={18} />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${t.shortName} down`}
                  disabled={i === pinned.length - 1 || busyId === t.activityTypeId}
                  onClick={() => onMove(t.activityTypeId, "down")}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"
                >
                  <ChevronDown size={18} />
                </button>
                <button
                  type="button"
                  aria-label={`Unpin ${t.shortName}`}
                  disabled={busyId === t.activityTypeId}
                  onClick={() => onUnpin(t.activityTypeId)}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-(--accent) hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Pin size={16} fill="currentColor" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          All activities
        </h3>
        {rest.length === 0 ? (
          <p className="text-sm italic text-gray-400">Every activity is pinned.</p>
        ) : (
          <ul className="space-y-2">
            {rest.map((t) => (
              <li
                key={t.activityTypeId}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50"
              >
                <ActivityIcon name={t.iconName} size={18} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-300">{t.name}</span>
                <button
                  type="button"
                  aria-label={`Pin ${t.shortName}`}
                  disabled={busyId === t.activityTypeId}
                  onClick={() => onPin(t.activityTypeId)}
                  className="flex h-11 w-11 items-center justify-center rounded-md text-gray-400 hover:text-(--accent) hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Pin size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
